/**
 * ddStudio — hero intro choreography, scroll reveals, playful micro-interactions
 * and quick add to cart for the dd-* sections.
 *
 * Quick add posts to /cart/add and dispatches Horizon's CartAddEvent, so the
 * cart drawer and cart bubble update exactly like a product-form add.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

clearTimeout(window.__ddFailsafe);

const root = document.documentElement;
const motionOK = () => root.classList.contains('dd-motion');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const BRAND_COLORS = ['#C6C7FF', '#FEEC86', '#0264FD', '#953140', '#001433'];
const REVEAL_SELECTOR =
  '[data-dd-reveal], .dd-duo__ladybird, .dd-duo__espionage, .dd-duo__sticker, .dd-footer__sticker, .dd-footer__chill';

/* ---------------------------------------------------------------- Hero */

/** @param {HTMLElement} hero */
function initHero(hero) {
  if (hero.dataset.ddInit) return;
  hero.dataset.ddInit = 'true';

  const stage = /** @type {HTMLElement | null} */ (hero.querySelector('.dd-hero__stage'));
  const photos = /** @type {HTMLImageElement[]} */ ([
    ...hero.querySelectorAll('.dd-hero__back img, .dd-hero__front img'),
  ]);
  const decoded = Promise.all(photos.map((img) => (img.complete ? Promise.resolve() : img.decode().catch(() => {}))));
  const timeout = new Promise((resolve) => setTimeout(resolve, 1400));
  Promise.race([decoded, timeout]).then(() => requestAnimationFrame(() => hero.classList.add('is-ready')));

  if (!motionOK() || !stage) return;

  // Pointer parallax, eased towards the target each frame.
  let targetX = 0;
  let targetY = 0;
  let x = 0;
  let y = 0;
  let frame = 0;
  const tick = () => {
    x += (targetX - x) * 0.08;
    y += (targetY - y) * 0.08;
    stage.style.setProperty('--mx', x.toFixed(4));
    stage.style.setProperty('--my', y.toFixed(4));
    frame = Math.abs(targetX - x) + Math.abs(targetY - y) > 0.002 ? requestAnimationFrame(tick) : 0;
  };
  const kick = () => {
    if (!frame) frame = requestAnimationFrame(tick);
  };

  hero.addEventListener('pointermove', (event) => {
    if (!finePointer.matches) return;
    const rect = stage.getBoundingClientRect();
    targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    targetY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    kick();
  });
  hero.addEventListener('pointerleave', () => {
    targetX = 0;
    targetY = 0;
    kick();
  });

  // Scroll depth: 0 at rest, 1 once the stage has scrolled out of view.
  let scrollFrame = 0;
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      const rect = stage.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top / rect.height));
      stage.style.setProperty('--sy', progress.toFixed(4));
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ------------------------------------------------------------- Reveals */

const CLIPPED_REVEAL = '[data-dd-reveal="wipe"], [data-dd-reveal="clip"]';

/** Trigger element -> elements it reveals. */
const revealGroups = new Map();

/** @param {Element} trigger */
function revealGroup(trigger) {
  revealGroups.get(trigger)?.forEach((element) => element.classList.add('is-in'));
  revealGroups.delete(trigger);
  revealObserver?.unobserve(trigger);
}

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) revealGroup(entry.target);
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      )
    : null;

// Safety net for engines where IntersectionObserver misses clipped/transformed
// targets: reveal any pending group whose trigger box has entered the viewport.
let revealCheckFrame = 0;
const checkPendingReveals = () => {
  revealCheckFrame = 0;
  // At the very bottom of the page nothing can scroll further up, so reveal everything on screen.
  const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
  const limit = atBottom ? window.innerHeight : window.innerHeight * 0.92;
  for (const trigger of [...revealGroups.keys()]) {
    const rect = trigger.getBoundingClientRect();
    if (rect.top < limit && rect.bottom > 0) revealGroup(trigger);
  }
};
const scheduleRevealCheck = () => {
  if (!revealCheckFrame && revealGroups.size) revealCheckFrame = requestAnimationFrame(checkPendingReveals);
};
window.addEventListener('scroll', scheduleRevealCheck, { passive: true });
window.addEventListener('resize', scheduleRevealCheck);
window.addEventListener('load', scheduleRevealCheck);
window.addEventListener('beforeprint', () => [...revealGroups.keys()].forEach(revealGroup));

/**
 * IntersectionObserver applies clip-path, so an element hidden by a clip-path reveal
 * (or sitting inside one) never reports as visible. Those are triggered by the
 * unclipped parent of the outermost clipped reveal instead.
 * @param {Element} element
 * @returns {Element}
 */
function revealTrigger(element) {
  const clippedAncestor = element.parentElement?.closest(CLIPPED_REVEAL);
  const base = clippedAncestor ?? element;
  return base.matches(CLIPPED_REVEAL) ? base.parentElement ?? base : base;
}

/** @param {ParentNode} scope */
function initReveals(scope) {
  const instant = !motionOK() || !revealObserver || window.Shopify?.designMode;
  scope.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
    if (element.classList.contains('is-in')) return;
    if (instant || !revealObserver) {
      element.classList.add('is-in');
      return;
    }
    const trigger = revealTrigger(element);
    let group = revealGroups.get(trigger);
    if (!group) {
      group = new Set();
      revealGroups.set(trigger, group);
      revealObserver.observe(trigger);
    }
    group.add(element);
  });
}

/* ------------------------------------------------------ Confetti burst */

/**
 * @param {number} x - viewport x
 * @param {number} y - viewport y
 * @param {number} [count]
 */
function burst(x, y, count = 12) {
  if (!motionOK()) return;
  for (let i = 0; i < count; i++) {
    const bit = document.createElement('span');
    bit.className = 'dd-confetti';
    bit.style.background = BRAND_COLORS[i % BRAND_COLORS.length];
    bit.style.borderRadius = i % 3 === 0 ? '50%' : '0';
    bit.style.left = `${x}px`;
    bit.style.top = `${y}px`;
    document.body.append(bit);

    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.6;
    const distance = 50 + Math.random() * 70;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance + 30;
    bit
      .animate(
        [
          { transform: 'translate(-50%, -50%) rotate(0deg) scale(1)', opacity: 1 },
          {
            transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${Math.random() * 540 - 270}deg) scale(0.5)`,
            opacity: 0,
          },
        ],
        { duration: 800 + Math.random() * 400, easing: 'cubic-bezier(.16,1,.3,1)' }
      )
      .finished.then(() => bit.remove(), () => bit.remove());
  }
}

/* ---------------------------------------------- Toys & magnetic buttons */

/** @param {ParentNode} scope */
function initToys(scope) {
  scope.querySelectorAll('.dd-toy').forEach((toy) => {
    if (!(toy instanceof HTMLElement) || toy.dataset.ddToy) return;
    toy.dataset.ddToy = 'true';

    toy.addEventListener('pointerenter', () => {
      if (!motionOK() || toy.getAnimations().some((animation) => animation.id === 'dd-wiggle')) return;
      const wiggle = toy.animate(
        [
          { rotate: '0deg', scale: 1 },
          { rotate: '-12deg', scale: 1.08 },
          { rotate: '9deg', scale: 1.08 },
          { rotate: '-4deg', scale: 1.04 },
          { rotate: '0deg', scale: 1 },
        ],
        { duration: 700, easing: 'ease-out' }
      );
      wiggle.id = 'dd-wiggle';
    });

    toy.addEventListener('click', (event) => {
      if (!motionOK()) return;
      toy.animate(
        [
          { rotate: '0deg', scale: 1 },
          { rotate: '360deg', scale: 1.25, offset: 0.6 },
          { rotate: '360deg', scale: 1 },
        ],
        { duration: 900, easing: 'cubic-bezier(.34,1.56,.64,1)' }
      );
      burst(event.clientX, event.clientY, 10);
    });
  });

  scope.querySelectorAll('[data-dd-magnetic]').forEach((element) => {
    if (!(element instanceof HTMLElement) || element.dataset.ddMagnetic === 'on') return;
    element.dataset.ddMagnetic = 'on';
    element.addEventListener('pointermove', (event) => {
      if (!motionOK() || !finePointer.matches) return;
      const rect = element.getBoundingClientRect();
      const dx = (event.clientX - (rect.left + rect.width / 2)) * 0.22;
      const dy = (event.clientY - (rect.top + rect.height / 2)) * 0.32;
      element.style.translate = `${dx.toFixed(1)}px ${dy.toFixed(1)}px`;
    });
    element.addEventListener('pointerleave', () => {
      element.style.translate = '';
    });
  });
}

/* ------------------------------------------------------------ Marquee */

/** @param {ParentNode} scope */
function initMarquees(scope) {
  if (!motionOK()) return;
  scope.querySelectorAll('.dd-marquee').forEach((marquee) => {
    if (!(marquee instanceof HTMLElement) || marquee.dataset.ddInit) return;
    marquee.dataset.ddInit = 'true';

    const tracks = [...marquee.querySelectorAll('.dd-marquee__track')];
    let hovering = false;
    let boost = 0;
    let lastY = window.scrollY;
    let frame = 0;

    const apply = () => {
      const rate = (hovering ? 0.3 : 1) + boost;
      for (const track of tracks) {
        for (const animation of track.getAnimations()) {
          if (/** @type {CSSAnimation} */ (animation).animationName === 'dd-marquee') animation.playbackRate = rate;
        }
      }
    };
    const decay = () => {
      boost *= 0.92;
      if (boost < 0.02) boost = 0;
      apply();
      frame = boost ? requestAnimationFrame(decay) : 0;
    };

    marquee.addEventListener('pointerenter', () => {
      hovering = true;
      apply();
    });
    marquee.addEventListener('pointerleave', () => {
      hovering = false;
      apply();
    });
    window.addEventListener(
      'scroll',
      () => {
        const delta = Math.abs(window.scrollY - lastY);
        lastY = window.scrollY;
        boost = Math.min(4, boost + delta / 60);
        if (!frame) frame = requestAnimationFrame(decay);
      },
      { passive: true }
    );
  });
}

/* ---------------------------------------------------------- Quick add */

/** @param {Element} [except] */
function closePanels(except) {
  document.querySelectorAll('.dd-card__visual.is-open').forEach((visual) => {
    if (visual === except) return;
    visual.classList.remove('is-open');
    visual.querySelector('[data-dd-toggle]')?.setAttribute('aria-expanded', 'false');
    const panel = visual.querySelector('.dd-card__variants');
    if (panel instanceof HTMLElement) panel.inert = true;
  });
}

/**
 * @param {HTMLElement} card
 * @param {string} message
 */
function showToast(card, message) {
  const visual = card.querySelector('.dd-card__visual');
  if (!visual) return;
  let toast = /** @type {HTMLElement | null} */ (visual.querySelector('.dd-card__toast'));
  if (!toast) {
    toast = document.createElement('p');
    toast.className = 'dd-card__toast';
    toast.setAttribute('role', 'alert');
    visual.append(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(Number(toast.dataset.timer));
  toast.dataset.timer = String(setTimeout(() => toast?.classList.remove('is-visible'), 3500));
}

/**
 * @param {string} variantId
 * @param {HTMLElement} card
 * @param {HTMLElement} trigger
 */
async function quickAdd(variantId, card, trigger) {
  const button = /** @type {HTMLElement | null} */ (card.querySelector('.dd-card__add-btn'));
  if (button?.classList.contains('is-loading')) return;

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  button?.classList.add('is-loading');
  button?.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });
    const data = await response.json();

    if (data.status) {
      card.dispatchEvent(new CartErrorEvent(card.id, data.message, data.description, data.errors));
      showToast(card, data.description || data.message);
      return;
    }

    const cart = await fetch('/cart.js')
      .then((result) => result.json())
      .catch(() => undefined);

    card.dispatchEvent(
      new CartAddEvent(cart, card.id, {
        source: 'product-form-component',
        itemCount: 1,
        productId: card.dataset.productId,
        variantId: String(variantId),
        sections: data.sections,
      })
    );

    closePanels();
    const origin = (trigger.isConnected ? trigger : button ?? trigger).getBoundingClientRect();
    burst(origin.left + origin.width / 2, origin.top + origin.height / 2, 14);
    button?.classList.add('is-added');
    setTimeout(() => button?.classList.remove('is-added'), 1800);

    const live = card.querySelector('[data-dd-live]');
    if (live) live.textContent = window.Theme?.translations?.added ?? 'Added to cart';
  } catch (error) {
    console.error(error);
    showToast(card, 'Something went wrong. Please try again.');
  } finally {
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const toggle = target.closest('[data-dd-toggle]');
  if (toggle) {
    const visual = toggle.closest('.dd-card__visual');
    if (!visual) return;
    const open = !visual.classList.contains('is-open');
    closePanels(visual);
    visual.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    const panel = visual.querySelector('.dd-card__variants');
    if (panel instanceof HTMLElement) panel.inert = !open;
    if (open) {
      const firstChip = /** @type {HTMLElement | null} */ (panel?.querySelector('.dd-chip:not([disabled])'));
      firstChip?.focus({ preventScroll: true });
    }
    return;
  }

  const add = /** @type {HTMLButtonElement | null} */ (target.closest('[data-dd-add]'));
  if (add) {
    event.preventDefault();
    const card = /** @type {HTMLElement | null} */ (add.closest('[data-dd-card]'));
    if (card && !add.disabled && add.dataset.ddAdd) quickAdd(add.dataset.ddAdd, card, add);
    return;
  }

  if (!target.closest('.dd-card__variants')) closePanels();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = document.querySelector('.dd-card__visual.is-open');
  if (!open) return;
  closePanels();
  /** @type {HTMLElement | null} */ (open.querySelector('[data-dd-toggle]'))?.focus();
});

/* --------------------------------------------------------------- Init */

/** @param {ParentNode} [scope] */
function init(scope = document) {
  scope.querySelectorAll('[data-dd-hero]').forEach((hero) => initHero(/** @type {HTMLElement} */ (hero)));
  initReveals(scope);
  initToys(scope);
  initMarquees(scope);
}

init();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
